import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { currentUser, logout, isAdmin } = useAuth();
  const { cartCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Indian Wear', path: '/indian-wear' },
    { name: 'Western Wear', path: '/western-wear' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-effect shadow-lg' : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="section-padding">
        <div className="container-max">
          <div className="flex items-center justify-between h-16 md:h-20 w-full">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 md:gap-3 shrink-0">
              <motion.img
                src={require('../assets/website_logo.png')}
                alt="House of Deepak Logo"
                className="h-10 md:h-14 w-auto"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              />
              {/* Removed brand text to keep navbar compact on small screens */}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium tracking-wide uppercase transition-colors duration-200 hover:text-warm-gray ${
                    location.pathname === link.path
                      ? 'text-deep-brown border-b-2 border-deep-brown'
                      : 'text-deep-brown/80'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center space-x-6">
              {/* Cart */}
              <Link to="/cart" className="relative">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative"
                >
                  <svg
                    className="w-6 h-6 text-deep-brown"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.293 1.293a1 1 0 00-.707 1.707v0a1 1 0 001 1h12M17 21a2 2 0 11-4 0 2 2 0 014 0zM9 21a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  {cartCount > 0 && (
                    <motion.span
                      className="absolute -top-2 -right-2 bg-deep-brown text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      {cartCount}
                    </motion.span>
                  )}
                </motion.div>
              </Link>

              {/* Profile Icon */}
              {currentUser && (
                <Link to="/profile">
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative"
                  >
                    <svg
                      className="w-6 h-6 text-deep-brown"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </motion.div>
                </Link>
              )}

              {/* User Menu */}
              {currentUser ? (
                <div className="flex items-center space-x-4">
                  {isAdmin() && (
                    <Link
                      to="/admin"
                      className="text-sm font-medium text-deep-brown/80 hover:text-deep-brown transition-colors duration-200"
                    >
                      Admin
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="text-sm font-medium text-deep-brown/80 hover:text-deep-brown transition-colors duration-200"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <Link
                    to="/login"
                    className="text-sm font-medium text-deep-brown/80 hover:text-deep-brown transition-colors duration-200"
                  >
                    Login
                  </Link>
                  <Link to="/register">
                    <button className="btn-primary text-xs py-2 px-4">
                      Register
                    </button>
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 shrink-0 relative z-10"
              aria-label="Toggle menu"
            >
              <div className="w-6 h-5 flex flex-col justify-between items-center">
                <motion.span
                  className="w-6 h-0.5 bg-deep-brown block"
                  animate={isOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
                  transition={{ duration: 0.3 }}
                />
                <motion.span
                  className="w-6 h-0.5 bg-deep-brown block"
                  animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
                <motion.span
                  className="w-6 h-0.5 bg-deep-brown block"
                  animate={isOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="md:hidden glass-effect border-t border-warm-gray/20 w-full"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="section-padding py-6">
              <div className="flex flex-col space-y-4 w-full">
                {/* Navigation Links */}
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-sm font-medium tracking-wide uppercase transition-colors duration-200 hover:text-warm-gray ${
                      location.pathname === link.path
                        ? 'text-deep-brown'
                        : 'text-deep-brown/80'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}

                <hr className="border-warm-gray/20" />

                {/* Cart */}
                <Link to="/cart" className="flex items-center justify-between">
                  <span className="text-sm font-medium text-deep-brown/80">Cart</span>
                  {cartCount > 0 && (
                    <span className="bg-deep-brown text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Link>

                {/* User Actions */}
                {currentUser ? (
                  <div className="flex flex-col space-y-4">
                    <Link
                      to="/profile"
                      className="text-sm font-medium text-deep-brown/80"
                    >
                      My Profile
                    </Link>
                    {isAdmin() && (
                      <Link
                        to="/admin"
                        className="text-sm font-medium text-deep-brown/80"
                      >
                        Admin Panel
                      </Link>
                    )}
                    <Link
                      to="/orders"
                      className="text-sm font-medium text-deep-brown/80"
                    >
                      My Orders
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="text-sm font-medium text-left text-deep-brown/80"
                    >
                      Logout
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col space-y-4">
                    <Link
                      to="/login"
                      className="text-sm font-medium text-deep-brown/80"
                    >
                      Login
                    </Link>
                    <Link to="/register">
                      <button className="btn-primary w-full text-sm">
                        Register
                      </button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;